import React, { useEffect } from "react";
import { Typography } from "@material-ui/core";
import { RBCardView } from "../components/RBCardView";

// Key used for cross-tab communication when Stripe onboarding completes
export const STRIPE_ONBOARDING_COMPLETE_KEY = "stripe_onboarding_complete";

interface StripeConnectStatusPageProps {
  title: string;
  message: string;
  mode: "success" | "error";
}

const StripeConnectStatusPage: React.FC<StripeConnectStatusPageProps> = ({
  title,
  message,
  mode,
}) => {
  const iconColor = mode === "success" ? "#36C6A5" : "#E53935";

  // Signal to other tabs that Stripe onboarding is complete
  useEffect(() => {
    if (mode === "success") {
      try {
        // Write to localStorage to trigger storage event in other tabs
        localStorage.setItem(STRIPE_ONBOARDING_COMPLETE_KEY, Date.now().toString());
        console.log("[StripeConnectThankYou] Signaled onboarding complete via localStorage");
      } catch (e) {
        console.error("[StripeConnectThankYou] Failed to signal completion:", e);
      }
    }
  }, [mode]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: "clamp(12px, 4vw, 36px)",
        boxSizing: "border-box",
      }}
    >
      <RBCardView
        style={{
          width: "100%",
          maxWidth: 760,
          padding: "clamp(16px, 4.5vw, 40px)",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "clamp(10px, 2.5vw, 24px)",
          }}
        >
          <svg
            width="clamp(52px, 16vw, 72px)"
            height="clamp(52px, 16vw, 72px)"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="9" stroke={iconColor} strokeWidth="1.6" />
            {mode === "success" ? (
              <path
                d="M8 12.2L10.6 14.8L16 9.4"
                stroke={iconColor}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <>
                <path
                  d="M9 9L15 15"
                  stroke={iconColor}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M15 9L9 15"
                  stroke={iconColor}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </>
            )}
          </svg>
        </div>

        <Typography
          style={{
            fontSize: "clamp(22px, 5.5vw, 34px)",
            marginBottom: "clamp(8px, 2vw, 20px)",
            textAlign: "center",
            fontWeight: "bold",
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>

        <Typography
          style={{
            fontSize: "clamp(14px, 3.6vw, 20px)",
            color: "#7A7A7A",
            textAlign: "center",
            lineHeight: 1.5,
            marginBottom: "clamp(6px, 1.5vw, 12px)",
            wordBreak: "break-word",
          }}
        >
          {message}
        </Typography>
      </RBCardView>
    </div>
  );
};

export const StripeConnectThankYou: React.FC = () => (
  <StripeConnectStatusPage
    mode="success"
    title="Stripe Setup Complete"
    message="Your Stripe account has been successfully set up. You may now close this page and continue using the app."
  />
);

export const StripeConnectError: React.FC = () => (
  <StripeConnectStatusPage
    mode="error"
    title="Stripe Setup Incomplete"
    message="We couldn't complete Stripe onboarding. Please return to the app and try again."
  />
);
